import type PageDataContext from "@core/Context/PageDataContext";
import FetchService from "@core-ui/ApiServices/FetchService";
import ApiUrlCreator from "@core-ui/ContextServices/ApiUrlCreator";
import PageDataContextService from "@core-ui/ContextServices/PageDataContext";
import parseContent from "@ext/bugsnag/logic/parseContent";
import sendBug from "@ext/bugsnag/logic/sendBug";
import ErrorConfirmService from "@ext/errorHandlers/client/ErrorConfirmService";
import DefaultError from "@ext/errorHandlers/logic/DefaultError";
import t from "@ext/localization/locale/translate";
import { getRecentSpans } from "@ext/loggers/opentelemetry";
import type { JSONContent } from "@tiptap/core";
import { useCallback } from "react";

type BugsnagDetails = {
	replacedArticle?: JSONContent;
	context?: Omit<PageDataContext, "conf" | "user"> & {
		conf: { branch: boolean; version: string };
		sourceDatas: null;
		user: Omit<PageDataContext["user"], "info"> & { info: null };
	};
};

export const useBugsnag = (itemLogicPath: string) => {
	const apiUrlCreator = ApiUrlCreator.value;
	const pageDataContext = PageDataContextService.value;

	// biome-ignore lint/correctness/useExhaustiveDependencies: context service values can change between renders
	const getPageData = useCallback(async (): Promise<JSONContent> => {
		const res = await FetchService.fetch(apiUrlCreator.getPageData(itemLogicPath));
		if (!res.ok) return;
		const pageData = await res.json();
		return JSON.parse(pageData.data.articleContentEdit);
	}, [itemLogicPath, apiUrlCreator]);

	const getDetails = useCallback((props: { editTree: JSONContent; context?: PageDataContext }) => {
		const { editTree, context } = props;
		const result: BugsnagDetails = {};

		if (editTree) result.replacedArticle = parseContent(editTree);

		if (context?.conf) {
			const conf = { branch: context.conf.isRelease, version: context.conf.version };
			result.context = { ...context, sourceDatas: null, user: { ...context.user, info: null }, conf };
		}

		return result;
	}, []);

	// biome-ignore lint/correctness/useExhaustiveDependencies: context service values can change between renders
	const getTechDetails = useCallback(async () => {
		const editTree = await getPageData();
		return getDetails({ editTree, context: pageDataContext });
	}, [getPageData, getDetails, pageDataContext]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: context service values can change between renders
	const sendLogsHandler = useCallback(
		async (comment: string, editTree: JSONContent) => {
			const details = getDetails({ editTree, context: pageDataContext });
			const logs = { comment, ...details };
			try {
				await sendBug(
					new Error("Пользовательская ошибка"),
					(e) => {
						e.addMetadata("props", logs);
						e.addMetadata("logs", getRecentSpans());
					},
					false,
				);
			} catch (e) {
				console.error(e);
				ErrorConfirmService.notify(
					new DefaultError(
						t("bug-report.error.cannot-send-feedback.message"),
						null,
						null,
						false,
						t("bug-report.error.cannot-send-feedback.title"),
					),
				);
			}
		},
		[getDetails, pageDataContext],
	);

	const onSubmit = useCallback(
		async (data: { description?: string; bAttach?: boolean }) => {
			const editTree = data.bAttach ? await getPageData() : null;
			await sendLogsHandler(data.description || "", editTree);
		},
		[getPageData, sendLogsHandler],
	);

	return { onSubmit, getTechDetails };
};
